/**
 * `/404` — and the catch-all every unmatched path resolves to.
 *
 * It is not a product surface: it is the fall-through the SPA history fallback
 * lands on when a route resolves to nothing. It still carries the whole shell,
 * for the inverse of the usual reason — a subscriber here is more lost than on
 * any other page, and the shell is the only route out this area owns.
 *
 * **Nothing is read on entry.** No query, no handler, no fixture: this surface
 * has no data, and adding a read would give it a loading state it cannot
 * justify.
 *
 * **No nav entry claims to be this page.** The current page is not a
 * destination, so no `aria-current="page"` appears anywhere in the shell — the
 * navigation links are exact-match `NavLink`s pointing at `/` and at the
 * public host, and none of them matches here. Marking one would send a
 * screen-reader user to a page that is not where they are.
 *
 * A valid session that fails an ownership check renders **this** page rather
 * than a refusal, mirroring the backend's 404-not-403 rule: a reader is never
 * told that something exists but is not theirs.
 */
import { useEffect } from 'react';
import { Link } from 'react-router-dom';

import { IconBrokenLink, IconHome, IconSearch } from '@shared/chrome/icons';

import {
  ERROR_ACTION_ICON,
  ERROR_ACTION_PRIMARY,
  ERROR_ACTION_SECONDARY,
  ErrorSurface,
} from '../_shared/ErrorSurface';
import { notFoundCopy } from './copy';

export function NotFoundPage() {
  useEffect(() => {
    // The one document-level attribute React is allowed to write. `lang`,
    // `dir` and the theme belong to `index.html` and are never touched here.
    document.title = notFoundCopy.documentTitle;
  }, []);

  return (
    <ErrorSurface
      kind="notfound"
      glyph={<IconBrokenLink />}
      heading={notFoundCopy.heading}
      body={notFoundCopy.body}
      actions={
        <>
          <Link to="/" className={ERROR_ACTION_PRIMARY}>
            <IconHome className={ERROR_ACTION_ICON} />
            {notFoundCopy.actions.home}
          </Link>
          {/*
            An INTERNAL route. `[x]` §18.7 — «a gated surface renders the
            public surface set on top of its session, never instead of it» —
            and this app owns `/search`. Leaving for the public host handed a
            signed-in reader a signed-out results page whose titles could not
            be opened in place, and discarded the client cache on the way.
          */}
          <Link to="/search" className={ERROR_ACTION_SECONDARY}>
            <IconSearch className={ERROR_ACTION_ICON} />
            {notFoundCopy.actions.search}
          </Link>
        </>
      }
    />
  );
}
